/**
 * Iter 74c — Star-Rated Booking Prompt.
 *
 * Renders under a completed/confirmed booking on the customer dashboard to
 * nudge the guest for a review. Two states:
 *
 *   1. Not-yet-reviewed → colourful star row with hover preview + one-tap
 *      submission. Any star click POSTs the rating immediately (empty
 *      comment). An "Add a comment" secondary link opens the parent
 *      Dialog with the chosen rating pre-filled so the guest can add
 *      words if they want to.
 *
 *   2. Already-reviewed → compact "You rated ⭐N" chip so the guest sees
 *      confirmation without any further action.
 *
 * Parent owns the Dialog state; this component just receives callbacks.
 */
import { useState } from "react";
import { Star, MessageSquarePlus, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewPromptCard({ booking, onReviewed, onOpenDialog }) {
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [localRating, setLocalRating] = useState(null);

  // Already reviewed → tiny confirmation chip.
  if (booking.has_review) {
    return (
      <div
        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold"
        data-testid={`review-done-${booking.id}`}
      >
        <CheckCircle2 className="h-3 w-3" />
        You rated
        <span className="inline-flex items-center gap-0.5">
          <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
          {booking.my_rating || 5}
        </span>
      </div>
    );
  }

  const oneTapSubmit = async (n) => {
    if (submitting) return;
    setSubmitting(true);
    setLocalRating(n);
    try {
      await api.post("/reviews", {
        car_id: booking.car_id,
        booking_id: booking.id,
        rating: n,
        comment: "",
      });
      toast.success(
        n >= 4
          ? "Thanks for the review — your host will love it!"
          : "Thanks — your review helps future guests."
      );
      onReviewed?.(booking.id, n);
    } catch (e) {
      toast.error(formatApiError(e));
      setLocalRating(null);
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hover || localRating || 0;

  return (
    <div
      className="mt-2 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-3 shadow-sm"
      data-testid={`review-prompt-${booking.id}`}
    >
      <div className="text-xs font-semibold text-amber-900 mb-1.5 flex items-center gap-1">
        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
        Rate your host — tap a star
      </div>
      <div
        className="flex gap-1"
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
            disabled={submitting}
            onMouseEnter={() => setHover(n)}
            onClick={() => oneTapSubmit(n)}
            className="p-0.5 rounded transition-transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60 disabled:cursor-wait"
            data-testid={`review-prompt-star-${n}-${booking.id}`}
          >
            <Star
              className={`h-6 w-6 ${
                n <= displayRating
                  ? "fill-amber-400 text-amber-400"
                  : "text-neutral-300"
              }`}
            />
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onOpenDialog?.(booking, hover || 5)}
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-900 hover:underline"
        data-testid={`review-prompt-comment-${booking.id}`}
      >
        <MessageSquarePlus className="h-3 w-3" />
        Add a written comment
      </button>
    </div>
  );
}
